import { Game } from './core/Game';
import { svgTextureCache } from './pixi/utils/SvgTextureCache';

const DEBUG_LOG_KEY = 'daynight_debug_logs';
const MAX_LOGS = 100;

// localStorage에 로그 저장 (크래시 후에도 확인 가능)
function saveLog(type: string, ...args: unknown[]): void {
    try {
        const msg = args.map(a => {
            if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ''}`;
            if (typeof a === 'object') {
                try { return JSON.stringify(a); } catch { return String(a); }
            }
            return String(a);
        }).join(' ');
        const time = new Date().toISOString();
        const entry = `[${time}] ${type}: ${msg}`;

        const logs = JSON.parse(localStorage.getItem(DEBUG_LOG_KEY) || '[]') as string[];
        logs.push(entry);
        if (logs.length > MAX_LOGS) logs.splice(0, logs.length - MAX_LOGS);
        localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(logs));
    } catch {
        // localStorage 실패 무시
    }
}

// iOS 디버그 콘솔 (URL에 ?debug 추가 시 활성화)
function setupDebugConsole(): void {
    const isDebug = window.location.search.includes('debug');
    if (!isDebug) return;

    // 이전 크래시 로그 표시
    const prevLogs = JSON.parse(localStorage.getItem(DEBUG_LOG_KEY) || '[]') as string[];

    // 새 세션 시작 표시
    saveLog('SESSION', '=== NEW SESSION START ===');

    const debugDiv = document.createElement('div');
    debugDiv.id = 'debug-console';
    debugDiv.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: 200px;
        background: rgba(0,0,0,0.95);
        color: #0f0;
        font-family: monospace;
        font-size: 9px;
        padding: 5px;
        overflow-y: auto;
        z-index: 99999;
        pointer-events: auto;
        white-space: pre-wrap;
        word-break: break-all;
    `;
    document.body.appendChild(debugDiv);

    // Clear 버튼 추가
    const clearBtn = document.createElement('button');
    clearBtn.textContent = 'Clear Logs';
    clearBtn.style.cssText = `
        position: fixed;
        bottom: 205px;
        right: 5px;
        z-index: 99999;
        padding: 5px 10px;
        font-size: 10px;
    `;
    clearBtn.onclick = () => {
        localStorage.removeItem(DEBUG_LOG_KEY);
        debugDiv.innerHTML = '<div style="color:#ff0">Logs cleared</div>';
    };
    document.body.appendChild(clearBtn);

    // 이전 로그 표시
    const renderLogs = () => {
        const logs = JSON.parse(localStorage.getItem(DEBUG_LOG_KEY) || '[]') as string[];
        debugDiv.innerHTML = logs.map(l => {
            const color = l.includes('ERR') || l.includes('UNCAUGHT') ? '#f00' :
                         l.includes('WARN') ? '#ff0' :
                         l.includes('SESSION') ? '#0ff' : '#0f0';
            return `<div style="color:${color};border-bottom:1px solid #333;padding:2px 0">${l}</div>`;
        }).join('');
        debugDiv.scrollTop = debugDiv.scrollHeight;
    };

    renderLogs();
    if (prevLogs.length > 0) {
        console.log(`Found ${prevLogs.length} previous logs (check above for crash info)`);
    }

    const origLog = console.log;
    const origWarn = console.warn;
    const origError = console.error;

    console.log = (...args) => { origLog(...args); saveLog('LOG', ...args); renderLogs(); };
    console.warn = (...args) => { origWarn(...args); saveLog('WARN', ...args); renderLogs(); };
    console.error = (...args) => { origError(...args); saveLog('ERR', ...args); renderLogs(); };

    window.onerror = (msg, src, line, col, err) => {
        saveLog('UNCAUGHT', `${msg} at ${src}:${line}:${col}`, err);
        renderLogs();
        return false;
    };

    window.onunhandledrejection = (e) => {
        saveLog('PROMISE', e.reason);
        renderLogs();
    };

    console.log('Debug console enabled - logs persist across crashes');

    // 메모리 모니터링 (1초마다)
    setInterval(() => {
        const perf = performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } };
        if (perf.memory) {
            const used = Math.round(perf.memory.usedJSHeapSize / 1024 / 1024);
            const limit = Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024);
            saveLog('MEM', `${used}MB / ${limit}MB`);
            renderLogs();
        }
    }, 2000);
}

// Wait for DOM and fonts to be ready
document.addEventListener('DOMContentLoaded', async () => {
    setupDebugConsole();

    try {
        // Preload Pretendard font with timeout for iOS Safari
        try {
            const font = new FontFace('Pretendard', 'url(/assets/font/Pretendard-Thin.woff2)');
            const fontLoadPromise = font.load();
            const timeoutPromise = new Promise<FontFace>((_, reject) =>
                setTimeout(() => reject(new Error('Font load timeout')), 3000)
            );

            await Promise.race([fontLoadPromise, timeoutPromise]);
            document.fonts.add(font);
        } catch (fontError) {
            console.warn('Font loading failed, using fallback:', fontError);
        }

        // Wait for fonts with timeout
        await Promise.race([
            document.fonts.ready,
            new Promise(resolve => setTimeout(resolve, 2000))
        ]);

        const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!canvas) {
            console.error('Canvas element not found');
            return;
        }

        const game = new Game(canvas);

        // Initialize PixiJS renderer
        await game.init();

        // Preload SVG textures (non-blocking)
        try {
            await Promise.race([
                svgTextureCache.preload(),
                new Promise(resolve => setTimeout(resolve, 3000))
            ]);
        } catch (svgError) {
            console.warn('SVG texture preload failed:', svgError);
        }

        // Wait for BGM to be fully loaded before starting (with timeout)
        try {
            await Promise.race([
                game.getSoundManager().loadBGM(),
                new Promise(resolve => setTimeout(resolve, 5000))
            ]);
        } catch (bgmError) {
            console.warn('BGM loading failed:', bgmError);
        }

        // 로딩 오버레이 제거
        const loadingOverlay = document.getElementById('loading-overlay');
        if (loadingOverlay) {
            loadingOverlay.style.transition = 'opacity 0.3s ease-out';
            loadingOverlay.style.opacity = '0';
            setTimeout(() => loadingOverlay.remove(), 300);
        }

        game.start();
    } catch (e) {
        console.error('Game initialization failed:', e);
        // 에러 시에도 로딩 오버레이 제거
        document.getElementById('loading-overlay')?.remove();
    }
});
